/**
 * Full-screen image detail view with shared-element transitions
 * - Image "lifts" from card and expands into view
 * - Metadata panel slides in after image settles
 * - Zoom/pan support for inspection
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { useGesture } from '@use-gesture/react';
import type { ImageMetadata } from '@/types';
import { variants } from '@/styles/animations';

interface ImageDetailProps {
  image: ImageMetadata;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onCompare?: () => void;
  onShowSimilar?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

export function ImageDetail({
  image,
  onClose,
  onPrevious,
  onNext,
  onCompare,
  onShowSimilar,
  hasPrevious = false,
  hasNext = false,
}: ImageDetailProps) {
  const [viewState, setViewState] = useState({ scale: 1, x: 0, y: 0 });
  const lastTapRef = useRef(0);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Reset view when image changes
  useEffect(() => {
    setViewState({ scale: 1, x: 0, y: 0 });
  }, [image.id]);

  const resetZoom = useCallback(() => {
    setViewState({ scale: 1, x: 0, y: 0 });
  }, []);

  const bind = useGesture(
    {
      onPinch: ({ offset: [s] }) => {
        setViewState((prev) => ({ ...prev, scale: s }));
      },
      onPinchEnd: ({ offset: [s] }) => {
        // Snap to 1x if close
        if (s < 1.1) {
          setViewState({ scale: 1, x: 0, y: 0 });
        }
      },
      onDrag: ({ movement: [mx, my], velocity: [vx], direction: [dx], cancel, tap, memo = { startScale: viewState.scale } }) => {
        if (tap) {
          // Double-tap detection
          const now = Date.now();
          if (now - lastTapRef.current < 300) {
            // Double-tap: toggle between 1x and 2.5x
            if (viewState.scale > 1) {
              setViewState({ scale: 1, x: 0, y: 0 });
            } else {
              setViewState({ scale: 2.5, x: 0, y: 0 });
            }
            lastTapRef.current = 0;
          } else {
            lastTapRef.current = now;
          }
          return memo;
        }

        if (memo.startScale > 1) {
          // Pan mode when zoomed
          setViewState((prev) => ({ ...prev, x: mx, y: my }));
        } else {
          // Swipe navigation when not zoomed
          if (Math.abs(vx) > 0.5) {
            if (dx > 0 && onPrevious) onPrevious();
            if (dx < 0 && onNext) onNext();
            cancel();
          }
        }
        return memo;
      },
    },
    {
      pinch: { scaleBounds: { min: 1, max: 5 }, rubberband: true },
      drag: { filterTaps: true, from: () => [viewState.x, viewState.y] },
    }
  );

  const isZoomed = viewState.scale > 1;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          if (isZoomed) {
            resetZoom();
          } else {
            onClose();
          }
          break;
        case 'ArrowLeft':
          onPrevious?.();
          break;
        case 'ArrowRight':
          onNext?.();
          break;
      }
    },
    [isZoomed, onClose, onPrevious, onNext, resetZoom]
  );

  // Listen for voice zoom commands
  useEffect(() => {
    const handleVoiceZoom = (e: CustomEvent<{ direction: 'in' | 'out' }>) => {
      if (e.detail.direction === 'in') {
        if (!isZoomed) {
          setViewState({ scale: 2.5, x: 0, y: 0 });
        }
      } else {
        resetZoom();
      }
    };

    window.addEventListener('voice-zoom', handleVoiceZoom as EventListener);
    return () => {
      window.removeEventListener('voice-zoom', handleVoiceZoom as EventListener);
    };
  }, [isZoomed, resetZoom]);

  return (
    <motion.div
      variants={variants.backdrop}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="fixed inset-0 z-50 bg-workshop-bg flex flex-col"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Header - slides in after image settles */}
      <motion.header
        variants={variants.detailPanel}
        initial="hidden"
        animate="visible"
        className="flex items-center justify-between px-6 py-4 bg-workshop-surface/80 backdrop-blur">
        <button
          onClick={onClose}
          className={clsx(
            'flex items-center gap-2 px-4 py-3 rounded-xl transition-colors',
            'text-white hover:bg-workshop-border',
            'min-h-touch'
          )}
        >
          <BackIcon />
          <span className="text-workshop-sm">Back</span>
        </button>

        <div className="flex items-center gap-3">
          {onCompare && image.beforeImageId && (
            <button
              onClick={onCompare}
              className={clsx(
                'px-4 py-3 rounded-xl transition-colors',
                'text-white hover:bg-workshop-border',
                'min-h-touch text-workshop-sm'
              )}
            >
              Compare
            </button>
          )}
          <button
            onClick={() => {
              if (isZoomed) {
                resetZoom();
              } else {
                setViewState({ scale: 2.5, x: 0, y: 0 });
              }
            }}
            className={clsx(
              'px-4 py-3 rounded-xl transition-colors',
              'text-white hover:bg-workshop-border',
              'min-h-touch text-workshop-sm'
            )}
          >
            {isZoomed ? 'Zoom Out' : 'Fullscreen'}
          </button>
        </div>
      </motion.header>

      {/* Image container - gesture-enabled */}
      <div className="flex-1 relative overflow-hidden">
        <div
          ref={imageContainerRef}
          {...bind()}
          className={clsx(
            'absolute inset-0',
            isZoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'
          )}
          style={{ touchAction: 'none' }}
        >
          <img
            src={image.url}
            alt={image.componentDetail}
            className="w-full h-full object-contain"
            style={{
              transform: `translate(${viewState.x}px, ${viewState.y}px) scale(${viewState.scale})`,
              transformOrigin: 'center center',
            }}
            draggable={false}
          />
        </div>

        {/* Navigation arrows */}
        {!isZoomed && (
          <>
            {hasPrevious && (
              <button
                onClick={onPrevious}
                className={clsx(
                  'absolute left-4 top-1/2 -translate-y-1/2',
                  'w-16 h-16 rounded-full bg-workshop-surface/80 backdrop-blur',
                  'flex items-center justify-center',
                  'text-white hover:bg-workshop-accent transition-colors'
                )}
              >
                <ChevronLeftIcon />
              </button>
            )}
            {hasNext && (
              <button
                onClick={onNext}
                className={clsx(
                  'absolute right-4 top-1/2 -translate-y-1/2',
                  'w-16 h-16 rounded-full bg-workshop-surface/80 backdrop-blur',
                  'flex items-center justify-center',
                  'text-white hover:bg-workshop-accent transition-colors'
                )}
              >
                <ChevronRightIcon />
              </button>
            )}
          </>
        )}
      </div>

      {/* Footer with metadata - slides in after image settles */}
      {!isZoomed && (
        <motion.footer
          variants={variants.detailPanel}
          initial="hidden"
          animate="visible"
          className="px-6 py-4 bg-workshop-surface/80 backdrop-blur"
        >
          <div className="flex items-start justify-between gap-6">
            <div className="space-y-2">
              <h2 className="text-display-sm text-white font-semibold">
                {image.componentDetail}
              </h2>
              <div className="flex flex-wrap gap-4 text-workshop-sm text-gray-300">
                <MetadataItem
                  label="Component"
                  value={image.component}
                  capitalize
                />
                <MetadataItem
                  label="Condition"
                  value={image.condition.replace('-', ' ')}
                  capitalize
                />
                <MetadataItem
                  label="Phase"
                  value={image.phase.replace('-', ' ')}
                  capitalize
                />
                <MetadataItem
                  label="Date"
                  value={new Date(image.capturedAt).toLocaleDateString()}
                />
                <MetadataItem
                  label="Confidence"
                  value={`${image.confidence}%`}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              {hasPrevious && (
                <button
                  onClick={onPrevious}
                  className={clsx(
                    'px-4 py-3 rounded-xl transition-colors',
                    'bg-workshop-border text-white hover:bg-workshop-accent',
                    'min-h-touch'
                  )}
                >
                  Previous
                </button>
              )}
              {onShowSimilar && (
                <button
                  onClick={onShowSimilar}
                  className={clsx(
                    'px-6 py-3 rounded-xl transition-colors',
                    'bg-workshop-accent text-white hover:bg-workshop-accent-hover',
                    'min-h-touch font-medium'
                  )}
                >
                  Show Similar
                </button>
              )}
              {hasNext && (
                <button
                  onClick={onNext}
                  className={clsx(
                    'px-4 py-3 rounded-xl transition-colors',
                    'bg-workshop-border text-white hover:bg-workshop-accent',
                    'min-h-touch'
                  )}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </motion.footer>
      )}
    </motion.div>
  );
}

function MetadataItem({
  label,
  value,
  capitalize = false,
}: {
  label: string;
  value: string;
  capitalize?: boolean;
}) {
  return (
    <div>
      <span className="text-gray-500">{label}:</span>{' '}
      <span className={capitalize ? 'capitalize' : ''}>{value}</span>
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 19l-7-7m0 0l7-7m-7 7h18"
      />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}
